import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { ShopConfig } from './api/types'
import { useSprache } from './i18n'
import { useLadenAussehen } from './theme'

/**
 * Die Ladenangaben stehen einmal für die ganze Anwendung bereit.
 *
 * Sie sind die eine Ausnahme von „jede Seite holt ihre Daten selbst": Name und
 * Währung stehen im Kopf, im Fuß und in jedem Preis. Sie auf jeder Seite neu
 * zu holen hieße, denselben Aufruf viermal zu machen, um viermal dasselbe zu
 * erfahren.
 *
 * Was hier bewusst nicht steht: eine Organisationskennung. Der Laden ergibt
 * sich aus dem Host der Anfrage, das Frontend erfährt ihn nie und kann ihn
 * damit auch nicht verwechseln.
 */
const ShopConfigContext = createContext<ShopConfig | null>(null)

export function ShopConfigProvider({
  config,
  children,
}: {
  config: ShopConfig
  children: ReactNode
}) {
  // Hier und nicht in einer eigenen Ebene: Das Aussehen steht in den
  // Grunddaten, also gilt es ab dem Augenblick, in dem es sie gibt. Ein
  // Nachladen gäbe es nicht zu sehen, aber ein Aufblitzen der Vorlagenfarbe.
  useLadenAussehen(config.theme)

  return <ShopConfigContext.Provider value={config}>{children}</ShopConfigContext.Provider>
}

export function useShopConfig(): ShopConfig {
  const config = useContext(ShopConfigContext)
  if (!config) throw new Error('useShopConfig außerhalb des ShopConfigProvider verwendet')
  return config
}

/**
 * Formatiert Bruttopreise in der Währung des Ladens.
 *
 * Die Zahl kommt fertig gerechnet vom Backend und wird hier nur noch
 * geschrieben — kein Runden, kein Umrechnen, keine Steuer. Intl kennt die
 * Regeln der jeweiligen Sprache (Komma oder Punkt, Abstand, voran- oder
 * nachgestelltes Zeichen) besser als jede Formatierung von Hand.
 *
 * Die *Schreibweise* hängt an der gewählten Sprache — „12,50 €" gegenüber
 * „€12.50". Die *Währung* nicht: Sie sagt der Server, und sie ist die des
 * Ladens. Wer Englisch liest, zahlt trotzdem in Euro; eine Umrechnung wäre
 * eine zweite Wahrheit über den Preis, und die abweichende fiele erst auf der
 * Rechnung auf.
 */
export function usePriceFormat(): (value: number) => string {
  const { currency } = useShopConfig()
  const { locale } = useSprache()
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EUR',
      }),
    [currency, locale],
  )
  return (value: number) => formatter.format(value)
}
